import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Calendar, Clock, Building2, Heart, ArrowLeft, UserRound, FileText, Download } from 'lucide-react'
import { citaService } from '../../services/citaService'
import LoadingState from '../../components/ui/LoadingState'
import EmptyState from '../../components/ui/EmptyState'
import MobileCard, { MobileCardContent } from '../../components/ui/MobileCard'
import { getTipoCitaLabel } from '../../constants/tipoCita'

export default function CitaDetailMobile() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [cita, setCita] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloadingCalendar, setDownloadingCalendar] = useState(false)

  useEffect(() => {
    const fetchCita = async () => {
      try {
        setLoading(true)
        const response = await citaService.getById(id)
        const data = response?.data?.data || response?.data || response
        setCita(data || null)
      } catch (error) {
        console.error('Error fetching cita detail:', error)
        setCita(null)
      } finally {
        setLoading(false)
      }
    }

    if (id) fetchCita()
  }, [id])

  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible'
    const date = new Date(dateString)
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })
  }

  const getStatusClass = (estado = '') => {
    const value = estado.toUpperCase()
    if (value.includes('COMPLET')) return 'bg-green-100 text-green-700'
    if (value.includes('RECHAZ')) return 'bg-red-100 text-red-700'
    if (value.includes('APROBAD')) return 'bg-blue-100 text-blue-700'
    return 'bg-yellow-100 text-yellow-700'
  }

  const handleDownloadCalendar = async () => {
    if (!id) return
    try {
      setDownloadingCalendar(true)
      await citaService.descargarCalendario(id)
    } catch (error) {
      console.error('Error downloading calendar file:', error)
    } finally {
      setDownloadingCalendar(false)
    }
  }

  if (loading) return <LoadingState message="Cargando cita..." />

  if (!cita) {
    return (
      <div className="px-4 py-6">
        <EmptyState
          icon={Calendar}
          title="Cita no encontrada"
          message="No se pudo cargar el detalle de la cita seleccionada."
        />
      </div>
    )
  }

  return (
    <div className="min-h-full bg-transparent pb-24">
      <div className="sticky top-0 z-20 bg-white/95 border-b border-pastel-pink backdrop-blur-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-white hover:bg-gray-100 transition-colors shadow-sm"
            aria-label="Volver"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Detalle de cita</h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="px-4 py-5 space-y-4">
        <MobileCard>
          <MobileCardContent className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {cita.mascota?.nombre || 'Mascota'}
                </h2>
                <p className="text-sm text-gray-600">
                  {cita.tipo_cita_etiqueta || getTipoCitaLabel(cita.tipo_cita || cita.tipoCita)}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(cita.estado)}`}>
                {cita.estado || 'Pendiente'}
              </span>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              {String(cita.estado || '')
                .toUpperCase()
                .includes('PENDIENTE_APROBACION') &&
                (cita.fecha_cita_propuesta ||
                  cita.fechaCitaPropuesta ||
                  cita.hora_cita_propuesta ||
                  cita.horaCitaPropuesta) && (
                <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 -mt-1 mb-1">
                  <p className="text-[11px] font-bold text-sky-900">Fecha y hora tentativa solicitada</p>
                  <p className="text-[12px] text-sky-950 mt-1">
                    {cita.fecha_cita_propuesta || cita.fechaCitaPropuesta
                      ? formatDate(cita.fecha_cita_propuesta || cita.fechaCitaPropuesta)
                      : '—'}
                    {cita.hora_cita_propuesta || cita.horaCitaPropuesta
                      ? ` · ${cita.hora_cita_propuesta || cita.horaCitaPropuesta}`
                      : ''}
                  </p>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-pastel-teal" />
                <span>{cita.mascota?.nombre || 'Mascota no especificada'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-pastel-teal" />
                <span>
                  {formatDate(
                    cita.fecha ||
                      cita.fecha_cita ||
                      cita.fechaCita ||
                      cita.fecha_cita_propuesta ||
                      cita.fechaCitaPropuesta
                  )}
                </span>
              </div>
              {(cita.hora ||
                cita.hora_cita ||
                cita.hora_cita_propuesta ||
                cita.horaCitaPropuesta) && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-pastel-teal" />
                  <span>
                    {cita.hora || cita.hora_cita || cita.hora_cita_propuesta || cita.horaCitaPropuesta}
                  </span>
                </div>
              )}
              {cita.clinica && (
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-pastel-teal" />
                  <span>{cita.clinica.nombre || cita.clinica.nombreClinica}</span>
                </div>
              )}
              {(cita.veterinario?.usuario || cita.veterinario) && (
                <div className="flex items-center gap-2">
                  <UserRound className="w-4 h-4 text-pastel-teal" />
                  <span>
                    {cita.veterinario?.usuario
                      ? `${cita.veterinario.usuario.nombre || ''} ${cita.veterinario.usuario.apellido || ''}`.trim()
                      : `${cita.veterinario?.nombre || ''} ${cita.veterinario?.apellido || ''}`.trim() || 'Veterinario'}
                  </span>
                </div>
              )}
              {(cita.descripcion || cita.descripcion_cita || cita.descripcionCita) && (
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-pastel-teal mt-0.5" />
                  <span>{cita.descripcion || cita.descripcion_cita || cita.descripcionCita}</span>
                </div>
              )}
            </div>

            <button
              onClick={handleDownloadCalendar}
              disabled={downloadingCalendar}
              type="button"
              className="w-full mt-1 h-11 rounded-xl bg-[#7a9a52] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Download className="w-4 h-4" />
              {downloadingCalendar ? 'Descargando...' : 'Descargar calendario'}
            </button>
          </MobileCardContent>
        </MobileCard>
      </div>
    </div>
  )
}

